import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { TextField } from '@mui/material'
import React from 'react'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { TODOIST_HELP_URL, TODOIST_ICON, TODOIST_REGISTER_URL } from '../common/integration-common'
import { TODOIST_STEPS, useIntegrationTodoistModel } from './integration-todoist-model'

const IntegrationTodoist = () => {
    const model = useIntegrationTodoistModel()

    return (
        <KarteraAdminTemplate loading={false} title="Integración con Todoist">
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title="Integración con Todoist"
                    description="Envía automáticamente las tareas de tu día a Todoist."
                    backTo={model.backTo}
                    backLabel="Integraciones"
                    actions={(
                        <a
                            href={TODOIST_HELP_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        >
                            <HelpOutlineIcon style={{ fontSize: 18 }} />
                            Ayuda
                        </a>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <div className="flex flex-wrap items-center gap-4">
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-k-line-soft bg-white p-2">
                            <img src={TODOIST_ICON} alt="Todoist" className="max-h-full max-w-full object-contain" />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="flex flex-wrap items-center gap-2">
                                <span className="text-base font-bold text-k-ink">Todoist*</span>
                                {!model.loading && (
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${model.connected ? 'bg-k-ok-soft text-k-ok' : 'bg-k-line-soft text-k-ink-2'}`}>
                                        {model.connected ? 'Conectado' : 'Sin conectar'}
                                    </span>
                                )}
                            </span>
                            <p className="m-0 text-sm text-k-ink-2">
                                Una de las aplicaciones para administrar tareas más sencillas de implementar. Kartera 365 se integra con ella siguiendo estos pasos.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Cómo conectarlo</h2>
                    <ol className="m-0 flex list-none flex-col gap-5 p-0">
                        {TODOIST_STEPS.map((step, index) => (
                            <li key={step.id} className="flex gap-3.5">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-k-accent-soft text-[13px] font-extrabold text-k-accent-ink">{index + 1}</span>
                                <div className="flex min-w-0 flex-1 flex-col gap-3 pt-0.5">
                                    <p className="m-0 text-sm text-k-ink-2">{step.text}</p>
                                    {step.id === 'registro' && (
                                        <a href={TODOIST_REGISTER_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 self-start text-sm font-bold text-k-primary-ink hover:underline">
                                            Crear cuenta en Todoist
                                            <OpenInNewOutlinedIcon style={{ fontSize: 16 }} />
                                        </a>
                                    )}
                                    {step.image && (
                                        <img src={step.image} alt={`Paso ${index + 1}`} className="max-w-full self-start rounded-xl border border-k-line" />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Token de API</h2>
                    <TextField
                        className="v2-field" variant="filled" fullWidth required
                        label="API Token"
                        autoComplete="off"
                        value={model.tokenValue}
                        helperText={model.connected ? 'Ya hay un token guardado. Pega uno nuevo para reemplazarlo.' : 'Pega aquí el token que copiaste de Todoist.'}
                        onChange={(event) => model.changeToken(event.target.value)}
                        onFocus={(event) => event.target.select()}
                    />
                </section>

                <div className="flex flex-wrap items-center justify-between gap-2.5 pt-1">
                    <p className="m-0 text-xs text-k-muted">* Derechos reservados © Doist Inc.</p>
                    <button
                        type="submit"
                        disabled={model.busy || !model.canSave}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar token
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default IntegrationTodoist
